import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { Alert, Button, Typography } from 'shared/ui'
import { Label } from 'shared/ui/Label'
import { makeInputNumeric, TextField } from 'shared/ui/TextField'
import { PriceInfo } from 'entities/Billing'
import { type ManageNumbersStore } from 'pages/settings/pages/numbers/ui/ManageNumbers/store/ManageNumbersStore'
import styles from './styles.module.scss'

type IManageNumbersModalProps = {
  manageNumbersStore: ManageNumbersStore
}

const MAX_LICENSES_COUNT = 9999

export const ManageNumbersModal = observer(({ manageNumbersStore }: IManageNumbersModalProps) => {
  const {
    licensesCount,
    addLicenseNumber,
    onAddLicenseNumberChange,
    onOpenLicenseRemoveModal,
    onLicenseBuy,
    onManageNumbersModalClose,
    priceInfoStore,
    requestInfoStatus,
    changeNumberStatus,
    disableButton,
    loadingButton,
    isGracePeriod,
    isPayNowByGracePeriod,
    isManageByGracePeriod,
    addLicenseNumberCount,
  } = manageNumbersStore

  const onBlur = () => {
    if (!addLicenseNumber) {
      onAddLicenseNumberChange('1')
    }
  }

  const NumericComponent = useMemo(
    () =>
      makeInputNumeric({
        allowNegative: false,
        decimalScale: 0,
        isAllowed: ({ value }) => {
          const count = parseInt(value)
          return Number.isNaN(count) || (count > 0 && count <= MAX_LICENSES_COUNT)
        },
      }),
    [licensesCount]
  )

  return (
    <div className={styles.root}>
      <div className={styles.notification}>
        {!isGracePeriod && (
          <Alert
            item={{
              type: 'infoBlue',
              desc: (
                <Typography ariaLabel={'title'} variant={'body-md-regular'}>
                  Your workspace has
                  <Typography ariaLabel={'title'} variant={'body-md-semibold'}>
                    &nbsp;{licensesCount} empty license{licensesCount > 1 ? 's' : ''}&nbsp;
                  </Typography>
                  available
                </Typography>
              ),
            }}
            rightActions={
              !!licensesCount
                ? [
                    {
                      variant: 'button',
                      type: 'primary',
                      text: 'Remove',
                      onClick: onOpenLicenseRemoveModal,
                    },
                  ]
                : undefined
            }
          />
        )}
        {isManageByGracePeriod && (
          <Alert
            item={{
              type: 'warning',
              title: `You have ${addLicenseNumberCount} unpaid licenses`,
              desc: (
                <Typography ariaLabel={'desc'} variant={'body-md-regular'}>
                  Once paid, you’ll be able to add or remove licenses anytime
                </Typography>
              ),
            }}
          />
        )}
        {isPayNowByGracePeriod && (
          <Alert
            item={{
              type: 'infoBlue',
              desc: (
                <Typography ariaLabel={'title'} variant={'body-md-regular'}>
                  Purchasing licenses will immediately end the license grace period.
                </Typography>
              ),
            }}
          />
        )}
        {requestInfoStatus.isFailed && (
          <Alert
            item={{
              type: 'error',
              desc: requestInfoStatus.error,
            }}
          />
        )}
        {changeNumberStatus.isFailed && (
          <Alert
            item={{
              type: 'error',
              desc: changeNumberStatus.error,
            }}
          />
        )}
      </div>
      <div>
        <Label
          label={{
            text: 'Add license',
          }}
        />
        <TextField
          Input={NumericComponent}
          size={'large'}
          onChange={onAddLicenseNumberChange}
          onBlur={onBlur}
          variant={'stroke'}
          value={addLicenseNumber}
          disabled={isGracePeriod}
          InputProps={{
            className: styles.search,
          }}
        />
      </div>

      <PriceInfo priceInfoStore={priceInfoStore} labelPrefix='Number licenses' />

      <div className={styles.actions}>
        <Button
          disabled={disableButton}
          typeBtn={'contained'}
          text={'Cancel'}
          size={'medium'}
          contained={'secondary'}
          textClassname={styles.button}
          onClick={onManageNumbersModalClose}
        />
        <Button
          disabled={disableButton}
          loading={loadingButton}
          typeBtn={'contained'}
          text={`Buy ${addLicenseNumber || 1} number license${
            parseInt(addLicenseNumber) > 1 ? 's' : ''
          }`}
          size={'medium'}
          textClassname={styles.button}
          onClick={onLicenseBuy}
        />
      </div>
    </div>
  )
})
